import React, { useCallback, useEffect, useMemo } from "react";
import { Button } from "reactstrap";
import { hasPasteMenuHighlight } from "utils";
import { useTranslate } from "hooks";
import { Icon, Spinner } from "components";
import {
  useIsFetching,
  useSelectedPasteItem,
  useTopology,
  useTopologyOperation,
  useWizardActions,
} from "stores/wizardStore";

const actions = {
  cutComponent: "pasteComponentAfterCut",
  copyConfiguration: "pasteConfiguration",
  copyComponent: "pasteComponent",
};

export const ZoomControls = ({ zoomIn, zoomOut, centerView, scale }) => {
  const translate = useTranslate();
  const { setSelectedPasteItem, invokeTopologyAction, cancelWizardAction } =
    useWizardActions();
  const selectedPasteItem = useSelectedPasteItem();
  const topologyOperation = useTopologyOperation();
  const topology = useTopology();
  const isActionFetching = useIsFetching();

  const handleZoomIn = useCallback(() => {
    zoomIn(0.2);
  }, [zoomIn]);
  const handleZoomOut = useCallback(() => {
    zoomOut(0.2);
  }, [zoomOut]);

  const handleFitContent = useCallback(() => {
    const transformComponent = document.getElementById("transformComponent");
    const content = document.getElementById("topologyWrapperId");

    const contentWidth = content.offsetWidth;
    const contentHeight = content.offsetHeight + 600;
    const transformComponentWidth = transformComponent.offsetWidth;
    const transformComponentHeight = transformComponent.offsetHeight;

    const scaleX = transformComponentWidth / contentWidth;
    const scaleY = transformComponentHeight / contentHeight;
    const newScale = Math.min(scaleX, scaleY);

    centerView(newScale);
  }, [centerView]);

  const pastedItemsLength = useMemo(
    () => selectedPasteItem?.size,
    [selectedPasteItem],
  );

  const cancelPasteMode = useCallback(() => {
    cancelWizardAction();
    if (hasPasteMenuHighlight(topology[0])) {
      if (pastedItemsLength > 0) {
        setSelectedPasteItem(new Set());
      }
    }
  }, [cancelWizardAction, topology, pastedItemsLength, setSelectedPasteItem]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        cancelPasteMode();
      }
    },
    [cancelPasteMode],
  );

  useEffect(() => {
    if (topologyOperation?.kind) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [topologyOperation, handleKeyDown]);

  const pasteComponent = useCallback(() => {
    if (isActionFetching) {
      return;
    }
    const { kind, item } = topologyOperation || {};

    invokeTopologyAction({
      action: actions[kind],
      sourceId: item,
    });
  }, [isActionFetching, topologyOperation, invokeTopologyAction]);

  const isDisabled = useMemo(
    () => isActionFetching || !pastedItemsLength || pastedItemsLength === 0,
    [isActionFetching, pastedItemsLength],
  );
  return (
    <div
      id="topologyZoomControls"
      className="position-absolute bg-white p-3 d-flex justify-content-between mx-auto align-items-center rounded shadow-sm"
      style={{
        bottom: "1rem",
        left: "50%",
        transform: "translateX(-50%)",
        minWidth: "254px",
      }}
    >
      {topologyOperation?.kind && (
        <div className="d-flex gap-2 align-items-center">
          <Button
            color="outline-dark"
            type="button"
            onClick={cancelPasteMode}
            disabled={isActionFetching}
          >
            {translate("CANCEL")}
          </Button>
          <Button
            color="primary"
            onClick={pasteComponent}
            disabled={isDisabled}
            className="d-flex align-items-center gap-2"
          >
            {translate("WIZARD_NODE_PASE_CONFIRMATION")}
            {isActionFetching && <Spinner size="xxs" />}
          </Button>
        </div>
      )}
      <Button onClick={handleFitContent} color="none" size="sm">
        <Icon
          className="text-gray-700"
          iconName="arrows-left-right-line-regular"
          data-testid="zoom-default"
          prefix="trox"
        />
      </Button>
      <div className="px-5_5 d-flex flex-nowrap align-items-center">
        <Button onClick={handleZoomOut} color="none" size="sm">
          <Icon
            iconName="minus-regular"
            prefix="trox"
            className="text-gray-700"
            data-testid="zoom-out"
          />
        </Button>
        <div className="border" style={{ width: "1px", height: "24px" }} />
        <Button onClick={handleZoomIn} color="none" size="sm">
          <Icon
            iconName="plus-regular"
            prefix="trox"
            className="text-gray-700"
            data-testid="zoom-in"
          />
        </Button>
      </div>
      <h6 className="text-gray-400 fs-16 m-0" style={{ minWidth: "44px" }}>
        {Math.round(scale * 100)} %
      </h6>
    </div>
  );
};
